import { Check, X, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { CrawlPage } from '../hooks/useCrawls'

/** Required and recommended properties per schema type (Google structured data guidelines) */
const SCHEMA_REQUIREMENTS: Record<string, { required: string[]; recommended: string[] }> = {
  Article: {
    required: ['headline', 'datePublished', 'author'],
    recommended: ['dateModified', 'image', 'publisher', 'description'],
  },
  NewsArticle: {
    required: ['headline', 'datePublished', 'author'],
    recommended: ['dateModified', 'image', 'publisher'],
  },
  BlogPosting: {
    required: ['headline', 'datePublished', 'author'],
    recommended: ['dateModified', 'image', 'publisher'],
  },
  LocalBusiness: {
    required: ['name', 'address'],
    recommended: ['telephone', 'openingHours', 'image', 'url', 'geo', 'priceRange'],
  },
  Organization: {
    required: ['name', 'url'],
    recommended: ['logo', 'contactPoint', 'sameAs', 'description'],
  },
  Product: {
    required: ['name'],
    recommended: ['image', 'description', 'offers', 'review', 'aggregateRating', 'brand', 'sku'],
  },
  WebSite: {
    required: ['name', 'url'],
    recommended: ['potentialAction', 'description'],
  },
  WebPage: {
    required: ['name'],
    recommended: ['description', 'breadcrumb', 'mainEntity'],
  },
  BreadcrumbList: {
    required: ['itemListElement'],
    recommended: [],
  },
  FAQPage: {
    required: ['mainEntity'],
    recommended: [],
  },
  HowTo: {
    required: ['name', 'step'],
    recommended: ['image', 'totalTime', 'estimatedCost'],
  },
  Recipe: {
    required: ['name'],
    recommended: ['image', 'author', 'datePublished', 'description', 'recipeIngredient', 'recipeInstructions'],
  },
  Event: {
    required: ['name', 'startDate', 'location'],
    recommended: ['endDate', 'image', 'description', 'offers', 'performer'],
  },
  Person: {
    required: ['name'],
    recommended: ['url', 'image', 'jobTitle', 'sameAs'],
  },
  Review: {
    required: ['itemReviewed', 'author'],
    recommended: ['reviewRating', 'datePublished'],
  },
  JobPosting: {
    required: ['title', 'description', 'datePosted', 'hiringOrganization'],
    recommended: ['validThrough', 'employmentType', 'jobLocation', 'baseSalary'],
  },
  Course: {
    required: ['name', 'description', 'provider'],
    recommended: ['offers', 'hasCourseInstance', 'coursePrerequisites'],
  },
  SoftwareApplication: {
    required: ['name'],
    recommended: ['operatingSystem', 'applicationCategory', 'offers', 'aggregateRating'],
  },
  VideoObject: {
    required: ['name', 'description', 'thumbnailUrl', 'uploadDate'],
    recommended: ['contentUrl', 'duration', 'embedUrl', 'publisher'],
  },
  Dataset: {
    required: ['name', 'description'],
    recommended: ['url', 'creator', 'license', 'distribution'],
  },
  Service: {
    required: ['name'],
    recommended: ['description', 'provider', 'areaServed', 'serviceType', 'offers'],
  },
  MedicalWebPage: {
    required: ['name'],
    recommended: ['about', 'lastReviewed', 'reviewedBy', 'medicalAudience'],
  },
}

/** Check if a property exists in a JSON-LD object (supports nested paths) */
function hasProperty(obj: Record<string, unknown>, prop: string): boolean {
  if (obj[prop] !== undefined && obj[prop] !== null && obj[prop] !== '') return true
  // Check @graph entries
  if (Array.isArray(obj['@graph'])) {
    return obj['@graph'].some((item: Record<string, unknown>) =>
      item[prop] !== undefined && item[prop] !== null && item[prop] !== ''
    )
  }
  return false
}

/** Extract all typed objects from a JSON-LD structure (handles @graph) */
function extractTypedObjects(schema: unknown): Array<{ type: string; obj: Record<string, unknown> }> {
  const results: Array<{ type: string; obj: Record<string, unknown> }> = []

  if (!schema || typeof schema !== 'object') return results

  const obj = schema as Record<string, unknown>

  if (typeof obj['@type'] === 'string') {
    results.push({ type: obj['@type'], obj })
  } else if (Array.isArray(obj['@type'])) {
    for (const t of obj['@type']) {
      if (typeof t === 'string') results.push({ type: t, obj })
    }
  }

  if (Array.isArray(obj['@graph'])) {
    for (const item of obj['@graph']) {
      results.push(...extractTypedObjects(item))
    }
  }

  if (Array.isArray(schema)) {
    for (const item of schema) {
      results.push(...extractTypedObjects(item))
    }
  }

  return results
}

interface ValidationResult {
  type: string
  required: Array<{ prop: string; present: boolean }>
  recommended: Array<{ prop: string; present: boolean }>
}

function validateSchema(schemas: unknown[]): ValidationResult[] {
  const results: ValidationResult[] = []

  for (const schema of schemas) {
    const typed = extractTypedObjects(schema)
    for (const { type, obj } of typed) {
      const spec = SCHEMA_REQUIREMENTS[type]
      if (!spec) continue // Unknown type, skip validation

      results.push({
        type,
        required: spec.required.map((prop) => ({ prop, present: hasProperty(obj, prop) })),
        recommended: spec.recommended.map((prop) => ({ prop, present: hasProperty(obj, prop) })),
      })
    }
  }

  return results
}

function tryParseSchemaData(raw: string | null): unknown[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch {
    return []
  }
}

export function SchemaValidation({ page }: { page: CrawlPage }) {
  const schemas = tryParseSchemaData(page.schema_data)
  const schemaTypes = (() => {
    try {
      return JSON.parse(page.schema_types || '[]') as string[]
    } catch {
      return []
    }
  })()

  if (!page.has_schema && schemaTypes.length === 0) {
    return (
      <div className="text-xs text-muted-foreground/50 italic">
        No structured data (JSON-LD) found on this page.
      </div>
    )
  }

  // If we have schema types but no full data (old crawl before schema_data capture)
  if (schemas.length === 0 && schemaTypes.length > 0) {
    return (
      <div className="space-y-2 text-xs">
        <div className="flex flex-wrap gap-1.5">
          {schemaTypes.map((type) => (
            <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
          ))}
        </div>
        <p className="text-muted-foreground italic">
          Full schema data not available (crawled before schema capture was enabled). Re-crawl to see validation.
        </p>
      </div>
    )
  }

  const validations = validateSchema(schemas)

  // Types found but none with known validation rules
  if (validations.length === 0 && schemaTypes.length > 0) {
    return (
      <div className="space-y-2 text-xs">
        <div className="flex flex-wrap gap-1.5">
          {schemaTypes.map((type) => (
            <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
          ))}
        </div>
        <p className="text-muted-foreground">Structured data present. No specific validation rules for these types.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {validations.map((v, i) => {
        const requiredMissing = v.required.filter((r) => !r.present).length
        const isValid = requiredMissing === 0

        return (
          <div key={`${v.type}-${i}`} className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant={isValid ? 'outline' : 'destructive'} className="text-xs">
                  {v.type}
                </Badge>
              </div>
              {isValid ? (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Valid
                </span>
              ) : (
                <span className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {requiredMissing} missing required
                </span>
              )}
            </div>

            {/* Required properties */}
            {v.required.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Required</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {v.required.map((r) => (
                    <span
                      key={r.prop}
                      className={`text-xs flex items-center gap-1 ${r.present ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}
                    >
                      {r.present ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      {r.prop}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recommended properties */}
            {v.recommended.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Recommended</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {v.recommended.map((r) => (
                    <span
                      key={r.prop}
                      className={`text-xs flex items-center gap-1 ${r.present ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}
                    >
                      {r.present ? <Check className="h-3 w-3" /> : <X className="h-3 w-3 opacity-40" />}
                      {r.prop}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      })}

      {/* Show any types without validation rules */}
      {schemaTypes.filter((t) => !validations.some((v) => v.type === t)).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {schemaTypes
            .filter((t) => !validations.some((v) => v.type === t))
            .map((type) => (
              <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
            ))}
        </div>
      )}
    </div>
  )
}
